import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../axiosInstance"; // your configured axios instance
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

/**
 * SignupPage component allows users to create a new account.
 * It includes fields for full name, phone number, password, OTP, and an optional referral code.
 * It handles OTP sending, countdown, and signup submission.
 */
const SignupPage = () => {
  const navigate = useNavigate();

  // State variables for form inputs
  const [signupFullName, setSignupFullName] = useState("");
  const [signupPhoneNumber, setSignupPhoneNumber] = useState("");
  // Removed signupEmail state
  const [signupPassword, setSignupPassword] = useState("");
  const [signupOtp, setSignupOtp] = useState("");
  const [signupReferralCode, setSignupReferralCode] = useState("");
  const [yourGeneratedReferralCode, setYourGeneratedReferralCode] =
    useState("");

  // State variables for UI feedback and control
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpCooldown, setOtpCooldown] = useState(0);

  // OTP cooldown countdown timer effect
  useEffect(() => {
    if (otpCooldown === 0) return;
    const timerId = setTimeout(() => setOtpCooldown(otpCooldown - 1), 1000);
    return () => clearTimeout(timerId);
  }, [otpCooldown]);

  // Function to validate phone number format for any country, mirroring backend logic
  const isValidPhoneNumber = (number) => {
    if (!number) return false;
    let phone = number.trim();

    // If it starts with '+', treat it as an international number
    if (phone.startsWith('+')) {
      const cleaned = '+' + phone.slice(1).replace(/\D/g, ''); // Keep '+' and remove non-digits
      // Basic length validation for international numbers (e.g., +1234567890)
      // A typical international number is between 8 and 16 digits (excluding '+')
      if (cleaned.length >= 9 && cleaned.length <= 17) { // '+' and 8-16 digits
          return true;
      }
      return false; // Invalid length for an international number
    }

    // Handle local Ethiopian numbers (assuming they don't start with '+')
    let cleanedPhone = phone.replace(/\D/g, ''); // Remove all non-digits

    // Convert 09... or 07... to 2519... or 2517...
    if (/^09\d{8}$/.test(cleanedPhone) || /^07\d{8}$/.test(cleanedPhone)) {
      return true;
    }
    // Already in 251 format
    if (/^2519\d{8}$/.test(cleanedPhone) || /^2517\d{8}$/.test(cleanedPhone)) {
      return true;
    }

    // If it's a 9-digit number starting with 9 or 7 (e.g., 912345678 without leading 0), assume Ethiopian
    if (/^(9|7)\d{8}$/.test(cleanedPhone) && cleanedPhone.length === 9) {
      return true;
    }

    // For any other format, return false. This is a strict approach to avoid
    // storing ambiguous or incomplete phone numbers.
    return false;
  };

  // Removed isValidEmail function

  // Generic input change handler clearing messages
  const handleInputChange = (setter) => (e) => {
    setter(e.target.value);
    setErrorMessage("");
    setSuccessMessage("");
  };

  // Send OTP handler
  const handleSendOtp = async () => {
    const phone = signupPhoneNumber.trim();

    if (!phone) {
      setErrorMessage("Please enter phone number to send OTP.");
      toast.error("Please enter phone number to send OTP.");
      return;
    }
    if (!isValidPhoneNumber(phone)) {
      setErrorMessage("Please enter a valid phone number (e.g., +1234567890 or 0912345678).");
      toast.error("Please enter a valid phone number (e.g., +1234567890 or 0912345678).");
      return;
    }
    if (otpCooldown > 0) {
      setErrorMessage(
        `Please wait ${otpCooldown} seconds before resending OTP.`
      );
      toast.warn(`Please wait ${otpCooldown} seconds before resending OTP.`);
      return;
    }

    setErrorMessage("");
    setSuccessMessage("");
    setLoading(true);

    try {
      const res = await axiosInstance.post("/auth/send-otp", {
        phoneNumber: phone,
        type: "signup",
      });

      if (res.data.success) {
        setOtpSent(true);
        setSuccessMessage("OTP sent successfully to your telegram!");
        setSignupOtp("");
        setOtpCooldown(30);
        toast.success("OTP sent successfully to your telegram!");
      } else {
        const message = res.data.message || "Failed to send OTP.";
        setErrorMessage(message);
        toast.error(message);
      }
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.message ||
        "Network or server error while sending OTP.";
      setErrorMessage(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Signup form submission handler
  const handleSignupSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    setSuccessMessage("");
    setLoading(true);

    const fullName = signupFullName.trim();
    const phone = signupPhoneNumber.trim();
    // Removed email from destructuring
    const password = signupPassword;
    const otp = signupOtp.trim();
    const referralCode = signupReferralCode.trim();

    // Removed !email from validation
    if (!fullName || !phone || !password || !otp) {
      setErrorMessage("Please fill in all required fields.");
      toast.error("Please fill in all required fields.");
      setLoading(false);
      return;
    }
    if (!isValidPhoneNumber(phone)) {
      setErrorMessage("Please enter a valid phone number (e.g., +1234567890 or 0912345678).");
      toast.error("Please enter a valid phone number (e.g., +1234567890 or 0912345678).");
      setLoading(false);
      return;
    }
    // Removed isValidEmail validation
    if (password.length < 6) {
      setErrorMessage("Password must be at least 6 characters.");
      toast.error("Password must be at least 6 characters.");
      setLoading(false);
      return;
    }
    if (!otpSent) {
      setErrorMessage("Please send OTP before signing up.");
      toast.error("Please send OTP before signing up.");
      setLoading(false);
      return;
    }

    try {
      const res = await axiosInstance.post("/auth/signup", {
        fullName,
        phoneNumber: phone,
        // Removed email from payload
        password,
        otp,
        referralCode: referralCode || null,
      });

      if (res.data.success) {
        setYourGeneratedReferralCode(res.data.yourReferralCode);
        setSuccessMessage("Signup successful! Redirecting to login...");
        toast.success("Signup successful! Please log in.");

        // Reset form fields and states
        setSignupFullName("");
        setSignupPhoneNumber("");
        // Removed setSignupEmail("");
        setSignupPassword("");
        setSignupOtp("");
        setSignupReferralCode("");
        setOtpSent(false);
        setOtpCooldown(0);

        setTimeout(() => navigate("/login"), 3000);
      } else {
        const message = res.data.message || "Signup failed.";
        setErrorMessage(message);
        toast.error(message);
      }
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.message ||
        "Network or server error during signup.";
      setErrorMessage(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100 p-4">
      <ToastContainer
        position="top-center"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      <div className="w-full max-w-md bg-white rounded-tr-[6rem] shadow-lg overflow-hidden">
        {/* Header Section */}
        <div className="relative bg-[#1570EF] h-40 flex items-center justify-center p-6 rounded-tr-[6rem]">
          <h2 className="text-3xl font-bold text-white text-center">
            Create Account
          </h2>
        </div>

        {/* Form Section */}
        <div className="p-8">
          <p className="text-center text-gray-600 mb-6">
            Sign up to get started
          </p>

          {/* Error and Success Messages */}
          {errorMessage && (
            <div
              className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md relative mb-4"
              role="alert"
            >
              <strong className="font-bold">Error!</strong>
              <span className="block sm:inline"> {errorMessage}</span>
            </div>
          )}
          {successMessage && (
            <div
              className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-md relative mb-4"
              role="alert"
            >
              <strong className="font-bold">Success!</strong>
              <span className="block sm:inline"> {successMessage}</span>
              {yourGeneratedReferralCode && (
                <p className="mt-2 text-sm">
                  Your Referral Code:{" "}
                  <span className="font-semibold">
                    {yourGeneratedReferralCode}
                  </span>
                </p>
              )}
            </div>
          )}

          <form onSubmit={handleSignupSubmit} className="space-y-4">
            {/* Full Name */}
            <div>
              <label
                htmlFor="fullName"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Full Name
              </label>
              <input
                id="fullName"
                type="text"
                value={signupFullName}
                onChange={handleInputChange(setSignupFullName)}
                placeholder="Enter your full name"
                className="w-full px-4 py-3 rounded-md border border-gray-300 bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
                disabled={loading}
                autoComplete="name"
              />
            </div>

            {/* Phone Number + OTP */}
            <div>
              <label
                htmlFor="phoneNumber"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Phone Number
              </label>
              {/* Help description for OTP */}
              <p className="text-sm text-gray-500 mb-2">
                To receive OTP, please start{" "}
                <a
                  href="https://t.me/Ethiobingos_bot"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#1570EF] hover:underline font-medium"
                >
                  @Ethiobingos_bot
                </a>{" "}
                on Telegram and link your phone number.
              </p>
              <div className="flex rounded-md shadow-sm">
                <input
                  id="phoneNumber"
                  type="tel"
                  value={signupPhoneNumber}
                  onChange={handleInputChange(setSignupPhoneNumber)}
                  placeholder="e.g., +1234567890 or 0912345678"
                  className="flex-1 px-4 py-3 rounded-l-md border border-gray-300 bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                  disabled={loading || otpSent}
                  autoComplete="tel"
                />
                <button
                  type="button"
                  onClick={handleSendOtp}
                  disabled={loading || otpCooldown > 0}
                  className={`px-4 py-3 rounded-r-md border border-l-0 border-blue-500 text-white font-semibold transition duration-300 ${
                    loading || otpCooldown > 0
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-[#1570EF] hover:bg-[#155ed6]"
                  }`}
                >
                  {loading && !otpSent
                    ? "Sending..."
                    : otpCooldown > 0
                    ? `Send OTP (${otpCooldown}s)`
                    : "Send OTP"}
                </button>
              </div>
            </div>

            {/* Password */}
            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Password
              </label>
              <input
                id="password"
                type="password"
                value={signupPassword}
                onChange={handleInputChange(setSignupPassword)}
                placeholder="••••••••"
                className="w-full px-4 py-3 rounded-md border border-gray-300 bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
                disabled={loading}
                autoComplete="new-password"
              />
            </div>

            {/* OTP */}
            <div>
              <label
                htmlFor="otp"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                OTP
              </label>
              <input
                id="otp"
                type="text"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                value={signupOtp}
                onChange={(e) => setSignupOtp(e.target.value)}
                placeholder="Enter OTP"
                className="w-full px-4 py-3 rounded-md border border-gray-300 bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={loading || !otpSent}
                autoComplete="one-time-code"
                required
              />
            </div>

            {/* Referral Code */}
            <div>
              <label
                htmlFor="referralCode"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Referral Code (Optional)
              </label>
              <input
                id="referralCode"
                type="text"
                value={signupReferralCode}
                onChange={handleInputChange(setSignupReferralCode)}
                placeholder="Enter referrer's code (Optional)"
                className="w-full px-4 py-3 rounded-md border border-gray-300 bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={loading}
                autoComplete="off"
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className={`w-full text-white font-semibold py-3 rounded-md transition duration-300 mt-2 ${
                loading
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-[#1570EF] hover:bg-[#155ed6]"
              }`}
            >
              {loading ? "Signing up..." : "Signup"}
            </button>
          </form>

          {/* Already have an account */}
          <div className="mt-4 text-center text-sm">
            Already have an account?{" "}
            <button
              onClick={() => navigate("/login")}
              className="text-[#1570EF] hover:underline font-medium"
              disabled={loading}
            >
              Log in
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;
